import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { ECHELON } from "../rendering/designTokens.ts";
import { createMascot } from "./OperatorMascot.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize } from "./uiTokens.ts";
import { ctaSystemCss } from "./ctaStyles.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";

export type VictoryCallback = () => void;

export class VictoryScreen {
  private overlay: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.overlay = document.createElement("div");
    this.overlay.id = "victory-screen";
    // BL-110 — modal dialog semantics for AT (the run is paused behind it); the
    // title (`#vt-title`, set in show()) names the dialog.
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-labelledby", "vt-title");
    this.injectStyles();
    parent.appendChild(this.overlay);
    this.overlay.style.display = "none";
  }

  show(
    score: number,
    wave: number,
    livesBonus: number,
    onExit: VictoryCallback,
    onRestart: VictoryCallback,
    onContinueEndless: VictoryCallback,
  ): void;

  show(
    score: number,
    wave: number,
    onExit: VictoryCallback,
    onRestart: VictoryCallback,
    onContinueEndless: VictoryCallback,
  ): void;

  show(
    score: number,
    wave: number,
    livesBonusOrOnExit: number | VictoryCallback,
    onExitOrRestart: VictoryCallback,
    onRestartOrContinue: VictoryCallback,
    maybeOnContinue?: VictoryCallback,
  ): void {
    const hasLivesBonus = typeof livesBonusOrOnExit === "number";
    const livesBonus = hasLivesBonus ? livesBonusOrOnExit : 0;
    const onExit = hasLivesBonus
      ? onExitOrRestart
      : (livesBonusOrOnExit as VictoryCallback);
    const onRestart = hasLivesBonus
      ? onRestartOrContinue
      : onExitOrRestart;
    const onContinueEndless = hasLivesBonus
      ? (maybeOnContinue as VictoryCallback)
      : onRestartOrContinue;

    this.overlay.innerHTML = "";
    this.overlay.style.display = "flex";
    this.overlay.classList.add("vt-enter");

    const gridBg = document.createElement("div");
    gridBg.className = "vt-grid";
    this.overlay.appendChild(gridBg);

    // S126 — the corruption washes OUT (Bible §3): her veil, dissolving once
    // over ~3 s as the clean green re-asserts. One-shot, slow, opacity-only.
    const purgeWash = document.createElement("div");
    purgeWash.className = "vt-purge-wash";
    this.overlay.appendChild(purgeWash);

    const content = document.createElement("div");
    content.className = "vt-content";

    // S130+ (Story Bible §6.5 follow-up) — the fixed ending-comic panel, an
    // authored static asset (scripts/generate-ending-comics.mjs; textless by
    // contract — this DOM carries the words). onerror removal keeps the screen
    // whole until the curated image is committed.
    const comic = document.createElement("img");
    comic.className = "vt-comic";
    comic.src = "/img/endings/ending-purged.webp";
    comic.alt = t("victory.comicAlt");
    comic.addEventListener("error", () => comic.remove());
    content.appendChild(comic);

    const title = document.createElement("h1");
    title.className = "vt-title";
    title.id = "vt-title";
    title.textContent = t("victory.title");
    content.appendChild(title);

    const sub = document.createElement("div");
    sub.className = "vt-sub";
    sub.textContent = t("victory.subtitle");
    content.appendChild(sub);

    const stats = document.createElement("div");
    stats.className = "vt-stats";
    // WOW Pass 2 (item 5) — the FINAL SCORE counts up as a rewarding reveal
    // (was a static number). The span is id'd so animateScore() can tick it;
    // tabular-nums keeps the digits from jittering during the count.
    stats.innerHTML = `
      <div class="vt-stat-row"><span>${t("victory.stat.wave")}</span><span>${wave}</span></div>
      <div class="vt-stat-row"><span>${t("victory.stat.score")}</span><span id="vt-final-score" class="vt-final-score-val">${score}</span></div>
      <div class="vt-stat-row vt-stat-bonus"><span>${t("score.bonus.label")}</span><span>${t("score.bonus.value", { value: livesBonus })}</span></div>
      <div class="vt-stat-row vt-stat-echelon"><span>${t("victory.stat.echelon")}</span><span class="vt-echelon-purged">${t("victory.stat.echelonPurged")}</span></div>
    `;
    content.appendChild(stats);
    const scoreEl = stats.querySelector("#vt-final-score");
    if (scoreEl instanceof HTMLElement) this.animateScore(scoreEl, score);

    // WOW Pass 2 (item 5) — loop the win back to the COMPETE framing: the score
    // is logged to the same GLOBAL DEFENSE LOG the start screen surfaces (the
    // seam where item-1 Game Center rank plugs in). Static (reduced-motion-safe).
    const standing = document.createElement("div");
    standing.className = "vt-standing";
    standing.textContent = t("score.logged");
    content.appendChild(standing);

    // S130 (Story Bible §6.5): winning means the Global Defense Log records it.
    const epilogue = document.createElement("div");
    epilogue.className = "vt-epilogue";
    epilogue.textContent = t("victory.epilogue");
    content.appendChild(epilogue);

    const row = document.createElement("div");
    row.className = "vt-btn-row";

    // BL-188 — ONE premium CTA language + ONE dominant primary. The old trio
    // shouted in three competing colours (AMBER exit / ICE_CYAN restart /
    // SENTINEL_GREEN continue, §A); now RESTART is the single SENTINEL_GREEN
    // primary (the focused action) and exit/continue are de-emphasised ghost
    // secondaries, so the eye lands on one action.
    const exitBtn = document.createElement("button");
    exitBtn.className = "vt-btn vt-btn-exit";
    exitBtn.textContent = t("victory.exit");
    exitBtn.addEventListener("click", () => {
      this.hide();
      onExit();
    });
    row.appendChild(exitBtn);

    const restartBtn = document.createElement("button");
    restartBtn.className = "vt-btn vt-btn-primary vt-btn-restart";
    restartBtn.innerHTML = `<span class="cta-foil"></span>${t("victory.restart")}`;
    restartBtn.addEventListener("click", () => {
      this.hide();
      onRestart();
    });
    row.appendChild(restartBtn);

    const continueBtn = document.createElement("button");
    continueBtn.className = "vt-btn vt-btn-continue";
    continueBtn.textContent = t("victory.continue");
    continueBtn.addEventListener("click", () => {
      this.hide();
      onContinueEndless();
    });
    row.appendChild(continueBtn);

    content.appendChild(row);

    // S130 (Story Bible §4): the endless continue IS Echo Mode — purged is not
    // erased; wave 36+ is canonically her residue, named under its button.
    const echoHint = document.createElement("div");
    echoHint.className = "vt-echo-hint";
    echoHint.textContent = t("victory.echoHint");
    content.appendChild(echoHint);

    // BL-122 — the cheering VERA + scowling FENRIR FLOW in a centered row
    // [VERA | content | FENRIR] (the same composition GameOver uses since its
    // Bug-15 fix), so they never overlap the content that grew with the S130
    // comic. Bottom-aligned, decorative, pointer-events:none.
    const veraMascot = createMascot({
      character: "vera",
      heroArt: "success", // S144/BL-135: triumphant VERA — the line held
      size: 190,
      frame: false,
      label: "SENTINEL-7",
      className: "vt-mascot vt-mascot-vera",
    });
    const fenrirMascot = createMascot({
      character: "fenrir",
      fenrirExpr: "defeated", // S144: the wolf is purged on victory
      size: 170,
      frame: false,
      label: "ORACLE: FENRIR",
      className: "vt-mascot vt-mascot-fenrir",
    });
    const flankRow = document.createElement("div");
    flankRow.className = "vt-row";
    flankRow.append(veraMascot, content, fenrirMascot);
    this.overlay.appendChild(flankRow);

    // BL-110 — move focus into the dialog so keyboard/SR users land on the
    // primary action (RESTART) and the dialog title is announced.
    restartBtn.focus();
  }

  hide(): void {
    this.overlay.style.display = "none";
    this.overlay.classList.remove("vt-enter");
  }

  /** WOW Pass 2 (item 5) — count the final score up as a reveal beat. Mirrors
   *  GameOverScreen.animateScore: reduced-motion (MotionSettings) lands on the
   *  final value at once (the count-up is motion, so it resolves to static). */
  private animateScore(el: HTMLElement, target: number): void {
    if (isReducedMotion()) {
      el.textContent = String(target);
      return;
    }
    const duration = 1200;
    const start = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = String(Math.floor(target * eased));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private injectStyles(): void {
    if (document.getElementById("victory-screen-styles")) return;
    const style = document.createElement("style");
    style.id = "victory-screen-styles";
    style.textContent = `
      #victory-screen {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(8, 12, 24, 0.94);
        z-index: ${UI_Z.END_SCREEN};
        font-family: ${UI_FONT_STACK};
        overflow: hidden;
      }
      .vt-enter {
        animation: vt-in 0.45s ease-out;
      }
      @keyframes vt-in {
        0% { opacity: 0; transform: scale(1.02); }
        100% { opacity: 1; transform: scale(1); }
      }
      .vt-grid {
        position: absolute;
        inset: 0;
        opacity: 0.08;
        pointer-events: none;
        background: repeating-linear-gradient(90deg, ${COLORS.SENTINEL_GREEN} 0px, transparent 1px, transparent 48px),
                    repeating-linear-gradient(0deg, ${COLORS.SENTINEL_GREEN} 0px, transparent 1px, transparent 48px);
      }
      /* S126: her veil dissolving — one slow opacity-only fade, then gone. */
      .vt-purge-wash {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 1;
        background: radial-gradient(ellipse at center, transparent 40%, ${ECHELON.SIGNAL}26 75%, ${ECHELON.DEEP}59 100%);
        opacity: 0;
        animation: vt-purge 3s ease-out forwards;
      }
      @keyframes vt-purge {
        0% { opacity: 1; }
        100% { opacity: 0; }
      }
      .vt-stat-echelon span:first-child {
        color: ${ECHELON.SIGNAL};
        opacity: 0.85;
      }
      .vt-echelon-purged {
        color: ${COLORS.SENTINEL_GREEN};
        text-shadow: 0 0 8px ${COLORS.SENTINEL_GREEN};
      }
      /* BL-122 — the row that flanks the content with the mascots, centred and
         bottom-aligned (mirrors GameOver's .go-row). */
      .vt-row {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        gap: 48px;
        max-width: 100%;
        max-height: 100%;
      }
      /* BL-122 — bounded column: the content can never exceed the fixed 900px
         design stage (index.html DESIGN_H), so the S130 comic + verdict + log
         line + stats + actions stay composed at every viewport and --ui-scale.
         876px leaves the stage edges clear; the comic is the only element that
         shrinks if the stack is ever taller than the band. */
      .vt-content {
        position: relative;
        z-index: 2;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        max-height: 876px;
        min-height: 0;
        box-sizing: border-box;
        /* BL-184 wrap guard on the bounded content column (comic/stats/lines). */
        overflow-wrap: break-word;
        word-break: normal;
      }
      /* S130+ — the PURGED ending panel (3:2 landscape, shipped at 2x). It is
         the flexible block (flex: 0 1 auto + min-height: 0): under vertical
         pressure it shrinks first, keeping the verdict/stats/actions on-screen. */
      .vt-comic {
        display: block;
        flex: 0 1 auto;
        min-height: 0;
        width: 360px;
        max-width: 100%;
        aspect-ratio: 3 / 2;
        margin: 0 auto 16px;
        border: 1px solid ${COLORS.SENTINEL_GREEN};
        box-shadow: 0 0 18px rgba(136, 224, 208, 0.35);
        object-fit: cover;
      }
      .vt-title {
        color: ${COLORS.SENTINEL_GREEN};
        letter-spacing: 7px;
        font-size: ${roleFontSize("display")};
        margin: 0 0 8px;
        text-shadow: 0 0 16px ${COLORS.SENTINEL_GREEN};
      }
      .vt-sub {
        color: ${COLORS.ICE_CYAN};
        letter-spacing: 3px;
        font-size: ${roleFontSize("caption")};
        margin-bottom: 20px;
        opacity: 0.95;
      }
      .vt-stats {
        margin: 14px auto 20px;
        width: 280px;
      }
      .vt-stat-row {
        display: flex;
        justify-content: space-between;
        color: ${COLORS.TEXT_PRIMARY};
        font-size: ${roleFontSize("label")};
        padding: 4px 0;
        border-bottom: 1px solid rgba(136, 224, 208, 0.2);
      }
      /* WOW Pass 2 (item 5) — the counting-up FINAL SCORE value: tabular-nums
         so the digits never jitter, a SENTINEL_GREEN highlight to read as the
         reveal's payoff. */
      .vt-final-score-val {
        color: ${COLORS.SENTINEL_GREEN};
        font-weight: bold;
        font-variant-numeric: tabular-nums;
        text-shadow: 0 0 8px rgba(136, 224, 208, 0.45);
      }
      /* WOW Pass 2 (item 5) — the compete-framing standing line (loops the win
         back to the GLOBAL DEFENSE LOG board). Static (reduced-motion-safe). */
      .vt-standing {
        color: ${COLORS.SENTINEL_GREEN};
        opacity: 0.82;
        font-size: ${roleFontSize("micro")};
        letter-spacing: 3px;
        margin: -8px 0 14px;
        text-shadow: 0 0 8px rgba(136, 224, 208, 0.4);
      }
      /* S130 — the defense-log epilogue + the Echo Mode naming (Bible §6.5). */
      .vt-epilogue {
        color: ${COLORS.TEXT_PRIMARY};
        font-size: ${roleFontSize("caption")};
        letter-spacing: 3px;
        margin: 0 0 18px;
        text-shadow: 0 0 10px rgba(136, 224, 208, 0.5);
      }
      .vt-echo-hint {
        color: ${COLORS.ICE_CYAN};
        opacity: 0.75;
        font-size: ${roleFontSize("micro")};
        letter-spacing: 2px;
        margin-top: 10px;
      }
      .vt-btn-row {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 14px;
      }
      /* BL-188 — the shared premium CTA material (primary = SENTINEL_GREEN
         RESTART; exit/continue inherit the ghost secondary rank). Replaces the
         three bare hover-invert boxes. */
      ${ctaSystemCss({
        scope: "#victory-screen",
        btnClass: "vt-btn",
        primaryClass: "vt-btn-primary",
        primaryColour: COLORS.SENTINEL_GREEN,
      })}
      /* BL-122 — mascots flow as bottom-aligned flex siblings of the content
         (no absolute offsets), so they can never overlap the content block. */
      .vt-mascot {
        flex: none;
        align-self: flex-end;
        z-index: 1;
        pointer-events: none;
      }
      /* (No viewport-size query: rendered inside the fixed 1600×900 stage and
         scaled to fit as one unit, so the layout stays intact on every device.) */

      /* BL-188 — reduced motion: the screen had NONE (only pointer:coarse). A
         reduced-motion user now gets the calm Victory (no scale-in entrance, the
         purge-wash resolved to its rest state, no grid). Mirrors GameOver's
         carve-out; the CTA motion is calmed by ctaSystemCss's own seam above. */
      @media (prefers-reduced-motion: reduce) {
        .vt-enter { animation: none !important; }
        .vt-purge-wash { animation: none !important; opacity: 0 !important; }
        .vt-grid { animation: none !important; }
      }
    `;
    document.head.appendChild(style);
  }
}
